import {
  AfterViewInit,
  ChangeDetectionStrategy, ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Observable } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';

@Component({
  selector: 'app-autocomplete-input',
  templateUrl: './autocomplete-input.component.html',
  styleUrls: ['./autocomplete-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteInputComponent),
      multi: true,
    }
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AutocompleteInputComponent implements ControlValueAccessor, AfterViewInit {

  @ViewChild('typeaheadInput') private inputRef: ElementRef<HTMLInputElement>;

  @Input() readonly id: string;
  @Input() readonly isInvalid: boolean = false;
  @Input() readonly title: string;
  @Input() readonly index: number;
  @Input() readonly editable: boolean;
  @Input('aria-label') readonly ariaLabel: string;
  @Input() readonly autocomplete: any;
  @Input() readonly inputFormatter: any;
  @Input() readonly resultFormatter: any;
  @Input() readonly debounceTime = 250;

  @Output() readonly itemSelect = new EventEmitter<any>();
  @Output() readonly focusOut = new EventEmitter<any>();
  // @Output() readonly keyUp = new EventEmitter<any>();

  public model: any;
  inputValue = '';
  loading = false;
  isDisabled = false;
  private selectedItem: any;
  // private readonly debounceTime = 250;

  search = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(this.debounceTime),
      distinctUntilChanged(),
      switchMap(term => {
        this.loading = true;
        this.ref.markForCheck();
        return this.autocomplete(term, this.index).pipe(
          tap((result: any[]) => {
            this.loading = false;
            if (!result.length && !this.editable) {
              this.propagateChange(null);
              this.selectedItem = null;
            }
            this.ref.markForCheck();
          })
        );
      })
    )

  private propagateChange = (_: any) => { };

  constructor(private ref: ChangeDetectorRef) { }

  ngAfterViewInit() {
    // ngbTypeahead (ng-bootstrap 4.x) sets role="combobox" together with aria-multiline,
    // which is a prohibited ARIA attribute for that role. Remove it after render.
    this.inputRef.nativeElement.removeAttribute('aria-multiline');
  }

  setDisabledState(isDisabled: boolean) {
    this.isDisabled = isDisabled;
  }

  writeValue(value: any) {
    if (!value) {
      this.inputValue = '';
      return;
    }
    if(typeof value === 'string'){
      this.inputValue = value;
    } else {
      this.inputValue = this.inputFormatter(value);
      this.selectedItem = value;
    }
    this.propagateChange(value);
  }

  registerOnChange(fn: any) {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {
  }

  onItemSelect(item: any) {
    this.selectedItem = item;
    this.itemSelect.emit(item);
  }

  onFocusOut(event: KeyboardEvent) {
    this.loading = false;
    const input: HTMLInputElement = <HTMLInputElement> event.target;
    if (!this.selectedItem && !this.editable) {
      this.focusOut.emit();
      input.value = '';
      return;
    }

    this.focusOut.emit(input.value);
  }

  onKeyUp(event: KeyboardEvent) {
    if (event.key !== 'Enter' && event.key !== 'Tab') {
      this.selectedItem = null;
    }
  }
}
